import React, { useEffect, useState } from 'react';
import { Loader2, X, CheckCircle2, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PublishValidationModal = ({ open, onClose, story, token, onConfirm }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !story?.id) return;
    let aborted = false;
    setResult(null);
    setError(null);
    setLoading(true);
    fetch(`${API_URL}/api/stories/${story.id}/publish-validation`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || `Validation failed (${r.status})`);
        }
        return r.json();
      })
      .then((data) => { if (!aborted) setResult(data); })
      .catch((e) => { if (!aborted) setError(e.message); })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [open, story?.id, token]);

  if (!open) return null;

  const allOk = result?.ok;

  return (
    <div
      data-testid="publish-validation-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-[#07743F]">
              Pre-Publish Check
            </div>
            <h3 className="font-serif text-lg font-bold text-gray-900 mt-1">
              {story?.title || 'Story'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="close-publish-validation"
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 text-gray-600 py-4 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Running validation…
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded p-3">
              {error}
            </div>
          )}

          {result && !loading && (
            <ul className="space-y-2">
              {result.checks?.map((c, i) => (
                <li
                  key={i}
                  data-testid={`publish-check-${i}`}
                  className={`flex items-start gap-2 p-2 rounded border text-sm ${
                    c.ok ? 'border-green-100 bg-green-50' : 'border-amber-200 bg-amber-50'
                  }`}
                >
                  {c.ok ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <div className="font-medium text-gray-900">{c.label}</div>
                    {!c.ok && c.reason && (
                      <div className="text-xs text-amber-800 mt-0.5">{c.reason}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="cancel-publish"
            className="px-4 py-1.5 rounded-full border border-gray-300 text-sm text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!allOk || loading}
            onClick={() => {
              onConfirm && onConfirm();
              onClose();
            }}
            data-testid="confirm-publish"
            className={`px-4 py-1.5 rounded-full text-sm text-white ${
              allOk && !loading
                ? 'bg-[#07743F] hover:bg-[#065a32]'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {allOk ? 'Publish Story' : 'Cannot Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishValidationModal;
